import {
  PARTICIPANT_COPY_TEXTS,
  participantCopyDefaults,
  participantCopyProblems,
} from '@nava/participant-copy';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  StudyPageContentPatchSchema,
  type StudyPageContent,
  type StudyPageContentPatch,
  type StudyPageLocale,
  type ExperienceProfile,
} from '@nava/backoffice-bff-contracts';
import { getExperienceDefaults, resolveExperienceCopy } from '@nava/participant-experience';
import { loadStudyPageContent, saveStudyPageContent } from '../api';

const studyPageCopyFields = PARTICIPANT_COPY_TEXTS.filter((text) =>
  ['landing', 'thankYou', 'everyPage'].includes(text.page)
);
const fields = [
  ['participantHeading', 'Landing heading'],
  ['participantTitle', 'Landing title'],
  ['participantDescription', 'Landing description'],
  ['participantEmailHeading', 'Email heading'],
  ['participantEmailBody', 'Email body'],
] as const;
const defaultKeys = {
  participantHeading: 'landing.title',
  participantTitle: '',
  participantDescription: '',
  participantEmailHeading: 'complete.participantEmail.title',
  participantEmailBody: 'complete.participantEmail.description',
};

interface Props {
  organizationId: string;
  study: string;
  profile?: ExperienceProfile | undefined;
  busy: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}
export function StudyPageContentEditor(props: Props) {
  const key = ['participant-experience', props.organizationId, 'content', props.study];
  const query = useQuery({
    queryKey: key,
    queryFn: () => loadStudyPageContent(props.organizationId, props.study),
    refetchOnWindowFocus: false,
  });
  return (
    <section className="space-y-4 rounded-xl border bg-white p-5">
      <h2 className="font-semibold">Study page content</h2>
      <p className="text-sm text-neutral-600">
        These texts belong only to this study. Save drafts here, then apply to the live study when
        ready.
      </p>
      {query.isPending ? (
        <p>Loading study page content...</p>
      ) : query.isError ? (
        <p role="alert">{query.error.message}</p>
      ) : (
        <StudyPageContentForm key={props.study} {...props} initial={query.data} />
      )}
    </section>
  );
}
export function StudyPageContentForm({ initial, ...props }: Props & { initial: StudyPageContent }) {
  const cache = useQueryClient();
  const [baseline, setBaseline] = useState(initial);
  const [locales, setLocales] = useState(initial.locales);
  const [locale, setLocale] = useState(initial.defaultLocale);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const dirty = JSON.stringify(locales) !== JSON.stringify(baseline.locales);
  useEffect(() => {
    props.onDirtyChange(dirty);
  }, [dirty, props.onDirtyChange]);
  useEffect(() => () => props.onDirtyChange(false), [props.onDirtyChange]);
  const mutation = useMutation({
    mutationFn: (patch: StudyPageContentPatch) =>
      saveStudyPageContent(props.organizationId, props.study, patch),
    onSuccess: (saved) => {
      setBaseline(saved);
      setLocales(saved.locales);
      setNotice('Study page content saved to draft.');
      cache.setQueryData(
        ['participant-experience', props.organizationId, 'content', props.study],
        saved
      );
    },
  });
  useEffect(() => {
    props.onBusyChange(mutation.isPending);
  }, [mutation.isPending, props.onBusyChange]);
  const value = locales[locale];
  const inherited = resolveExperienceCopy(initial.productLine, locale, props.profile?.overrides);
  const defaults = getExperienceDefaults(initial.productLine, locale);
  const copyDefaults = participantCopyDefaults(locale);
  const inheritedCopy = (key: (typeof studyPageCopyFields)[number]['key']) =>
    inherited[key] ?? copyDefaults[key];
  const setField = (key: keyof Omit<StudyPageLocale, 'participantCopy'>, text: string | null) =>
    setLocales((all) => ({ ...all, [locale]: { ...all[locale]!, [key]: text } }));
  const setCopy = (key: string, text: string | null) =>
    setLocales((all) => {
      const participantCopy = { ...all[locale]!.participantCopy };
      if (text === null) delete participantCopy[key];
      else participantCopy[key] = text;
      return { ...all, [locale]: { ...all[locale]!, participantCopy } };
    });
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        setError('');
        setNotice('');
        mutation.reset();
        const changes: StudyPageContentPatch['locales'] = {};
        for (const [language, current] of Object.entries(locales)) {
          const previous = baseline.locales[language]!;
          const patch: StudyPageContentPatch['locales'][string] = {};
          for (const [key] of fields) if (current[key] !== previous[key]) patch[key] = current[key];
          for (const key of new Set([
            ...Object.keys(previous.participantCopy),
            ...Object.keys(current.participantCopy),
          ])) {
            if (previous.participantCopy[key] !== current.participantCopy[key])
              (patch.participantCopy ??= {})[key] = current.participantCopy[key] ?? null;
          }
          if (Object.keys(patch).length) changes[language] = patch;
        }
        const result = StudyPageContentPatchSchema.safeParse({
          expectedRevision: baseline.revision,
          locales: changes,
        });
        if (!result.success)
          setError(
            result.error.issues
              .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
              .join('; ')
          );
        else {
          const problems = Object.values(result.data.locales).flatMap((change) =>
            participantCopyProblems(
              Object.fromEntries(
                Object.entries(change.participantCopy ?? {}).filter(([, text]) => text !== null)
              )
            )
          );
          if (problems.length)
            setError(problems.map((problem) => `${problem.key}: ${problem.problem}`).join('; '));
          else mutation.mutate(result.data);
        }
      }}
    >
      <fieldset disabled={props.busy || mutation.isPending} className="space-y-4">
        <label className="block">
          Study content language
          <select
            className="ml-2 rounded border p-2"
            value={locale}
            onChange={(event) => setLocale(event.target.value)}
          >
            {Object.keys(locales).map((language) => (
              <option key={language}>{language}</option>
            ))}
          </select>
        </label>
        {value &&
          ['Landing page', 'Finished page'].map((page, index) => (
            <section key={page} className="space-y-3">
              <h3 className="font-medium">{page}</h3>
              {fields
                .filter((_, i) => (index === 0 ? i < 3 : i >= 3))
                .map(([key, label]) => (
                  <div key={key}>
                    <label className="block">
                      {label}
                      <textarea
                        className="mt-1 block w-full rounded border p-2"
                        value={value[key] ?? ''}
                        placeholder={defaults[defaultKeys[key]] ?? 'Inherited study/product text'}
                        maxLength={
                          key === 'participantEmailHeading'
                            ? 100
                            : key === 'participantEmailBody'
                              ? 500
                              : undefined
                        }
                        onChange={(event) => setField(key, event.target.value || null)}
                      />
                    </label>
                    <button
                      type="button"
                      className="text-sm underline"
                      onClick={() => setField(key, null)}
                    >
                      Reset {label.toLowerCase()}
                    </button>
                    {value[key] === null && (
                      <p className="text-xs text-neutral-500">
                        Inherited: {defaults[defaultKeys[key]] || 'Study/product default'}
                      </p>
                    )}
                  </div>
                ))}
              {initial.productLine === 'customer_success' &&
                studyPageCopyFields
                  .filter((entry) =>
                    index === 0
                      ? entry.page === 'landing' || entry.page === 'everyPage'
                      : entry.page === 'thankYou'
                  )
                  .map((entry) => (
                    <div key={entry.key}>
                      <label className="block">
                        {entry.key}
                        <textarea
                          className="mt-1 block w-full rounded border p-2"
                          value={value.participantCopy[entry.key] ?? ''}
                          placeholder={inheritedCopy(entry.key)}
                          maxLength={entry.maxLength}
                          onChange={(event) => setCopy(entry.key, event.target.value)}
                        />
                      </label>
                      <p className="text-xs text-neutral-500">
                        {value.participantCopy[entry.key] === undefined
                          ? `Inherited: ${inheritedCopy(entry.key)}`
                          : entry.locked
                            ? 'Required disclosure text.'
                            : 'An empty override hides this text.'}
                      </p>
                      <button
                        type="button"
                        className="text-sm underline"
                        onClick={() => setCopy(entry.key, null)}
                      >
                        Reset {entry.key}
                      </button>
                    </div>
                  ))}
            </section>
          ))}
        {(error || mutation.error) && (
          <p role="alert" className="text-red-700">
            {error || mutation.error?.message}
          </p>
        )}
        {notice && <p role="status">{notice}</p>}
        <button
          type="submit"
          disabled={!dirty}
          className="rounded bg-violet-700 px-4 py-2 text-white disabled:opacity-50"
        >
          Save study page content
        </button>
      </fieldset>
    </form>
  );
}
